"use client"

import { useEffect, useState } from "react"
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts"
import "./Estadistica.css"
import { RefreshCw, DollarSign, Car, Clock, CreditCard } from "lucide-react"
import { fetchTickets } from "../../services/estadisticaService"
import { startOfDay, startOfWeek, startOfMonth } from "date-fns"

const Estadistica = () => {
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState("semana")
  const [refreshing, setRefreshing] = useState(false)

  // 1. Nuevo estado para manejar el rango de fechas de forma explícita
  const [dateRange, setDateRange] = useState({
    from: startOfWeek(new Date(), { weekStartsOn: 1 }),
    to: new Date(),
  })

  // 2. useEffect para obtener datos del API basado en el dateRange
  useEffect(() => {
    const fetchData = async () => {
      if (!dateRange.from || !dateRange.to) return

      setLoading(true)
      try {
        const data = await fetchTickets(dateRange) // Usar la función del servicio con el rango
        setTickets(data)
      } catch (error) {
        console.error("Error al cargar las estadísticas:", error)
        setTickets([]) // Limpiar datos en caso de error
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [dateRange]) // Se ejecuta cada vez que el rango de fechas cambia

  const handleRefresh = async () => {
    if (!dateRange.from || !dateRange.to) return
    setRefreshing(true)
    try {
      const data = await fetchTickets(dateRange)
      setTickets(data)
    } catch (error) {
      console.error("Error al refrescar:", error)
    } finally {
      setRefreshing(false)
    }
  }

  // 4. Actualizamos el manejador de los tabs para que modifique el dateRange
  const handleTabChange = (newTimeRange) => {
    setTimeRange(newTimeRange)
    const today = new Date()
    let fromDate

    switch (newTimeRange) {
      case "dia":
        fromDate = startOfDay(today)
        break
      case "semana":
        fromDate = startOfWeek(today, { weekStartsOn: 1 })
        break
      case "mes":
        fromDate = startOfMonth(today)
        break
      default:
        fromDate = startOfWeek(today, { weekStartsOn: 1 })
    }
    setDateRange({ from: fromDate, to: today })
  }

  // --- El resto de la lógica ahora opera directamente sobre el estado `tickets` ---
  // --- que ya viene pre-filtrado desde el backend. ---

  // Calcular estadísticas generales
  const totalIngresos = tickets.reduce((sum, ticket) => sum + (ticket.monto || 0), 0).toFixed(2)
  const totalTickets = tickets.length
  const ticketsEfectivo = tickets.filter((ticket) => ticket.tipo_pago === "E").length
  const ticketsTransferencia = tickets.filter((ticket) => ticket.tipo_pago === "T").length
  const ticketsTC = tickets.filter((ticket) => ticket.tipo_pago === "TC").length

  const calcularTiempoPromedio = () => {
    if (tickets.length === 0) return 0
    let tiempoTotal = 0
    let ticketsValidos = 0

    tickets.forEach((ticket) => {
      if (ticket.hentrada && ticket.hsalida) {
        const entrada = new Date(ticket.hentrada)
        const salida = new Date(ticket.hsalida)
        const diferencia = (salida.getTime() - entrada.getTime()) / (1000 * 60)
        tiempoTotal += diferencia
        ticketsValidos++
      }
    })
    return ticketsValidos > 0 ? tiempoTotal / ticketsValidos : 0
  }

  const tiempoPromedio = calcularTiempoPromedio()
  const horasPromedio = Math.floor(tiempoPromedio / 60)
  const minutosPromedio = Math.round(tiempoPromedio % 60)

  // Preparar datos para el gráfico de ingresos
  const prepararDatosGrafico = () => {
    if (tickets.length === 0) return []

    const formatoFecha = { day: "2-digit", month: "short" }
    const datosAgrupados = {}

    tickets.forEach((ticket) => {
      const fechaTicket = new Date(ticket.hsalida || ticket.hentrada)
      // Clave de agrupación basada en el día para mostrar en el gráfico
      const claveFecha = fechaTicket.toLocaleDateString("es-ES", formatoFecha)

      if (!datosAgrupados[claveFecha]) {
        datosAgrupados[claveFecha] = { fecha: claveFecha, ingresos: 0, cantidad: 0 }
      }
      datosAgrupados[claveFecha].ingresos += ticket.monto || 0
      datosAgrupados[claveFecha].cantidad += 1
    })

    return Object.values(datosAgrupados)
  }

  // Preparar datos para gráfico por tipo de pago
  const prepararDatosTipoPago = () => {
    return [
      { name: "Efectivo", value: ticketsEfectivo, color: "#1775ad" },
      { name: "Transferencia", value: ticketsTransferencia, color: "#933155" },
      { name: "Tarjeta", value: ticketsTC, color: "#e17b1d" },
    ]
  }

  const formatearFechaHora = (fechaStr) => {
    if (!fechaStr) return "N/A"
    try {
      return new Date(fechaStr).toLocaleString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    } catch (e) {
      return "Fecha inválida"
    }
  }

  const calcularDuracion = (entrada, salida) => {
    if (!entrada || !salida) return "N/A"
    const duracionMinutos = Math.floor((new Date(salida) - new Date(entrada)) / (1000 * 60))
    return `${Math.floor(duracionMinutos / 60)}h ${duracionMinutos % 60}m`
  }

  const obtenerTicketsRecientes = () => {
    return [...tickets].sort((a, b) => new Date(b.hsalida) - new Date(a.hsalida)).slice(0, 5)
  }

  const datosGrafico = prepararDatosGrafico()
  const datosTipoPago = prepararDatosTipoPago()
  const ticketsRecientes = obtenerTicketsRecientes()

  const obtenerTituloGrafico = () => {
    switch (timeRange) {
      case "dia":
        return "Ingresos de Hoy"
      case "semana":
        return "Ingresos de la Semana"
      case "mes":
        return "Ingresos del Mes"
      default:
        return "Ingresos"
    }
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Estadísticas</h1>
        <div className="tabs">
          <div className="tabs-list">
            <button className={`tab ${timeRange === "dia" ? "active" : ""}`} onClick={() => handleTabChange("dia")}>
              Día
            </button>
            <button
              className={`tab ${timeRange === "semana" ? "active" : ""}`}
              onClick={() => handleTabChange("semana")}
            >
              Semana
            </button>
            <button className={`tab ${timeRange === "mes" ? "active" : ""}`} onClick={() => handleTabChange("mes")}>
              Mes
            </button>
          </div>
        </div>
      </div>
      <button
        className="icon-button refresh-button"
        onClick={handleRefresh}
        disabled={refreshing || loading}
        title="Actualizar"
      >
        <RefreshCw className={`${refreshing || loading ? "animate-spin" : ""}`} />
      </button>

      {/* Tarjetas de estadísticas */}
      <div className="stats-cards">
        <div className="card">
          <div className="card-header">
            <h3>Total Ingresos</h3>
            <span className="icon">
              <DollarSign size={18} />
            </span>
          </div>
          <div className="card-content">
            <div className="stat-value">${totalIngresos}</div>
            <p className="stat-description">Ingresos en el período</p>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Total Vehículos</h3>
            <span className="icon">
              <Car size={18} />
            </span>
          </div>
          <div className="card-content">
            <div className="stat-value">{totalTickets}</div>
            <p className="stat-description">Vehículos en el período</p>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Tiempo Promedio</h3>
            <span className="icon">
              <Clock size={18} />
            </span>
          </div>
          <div className="card-content">
            <div className="stat-value">
              {horasPromedio}h {minutosPromedio}m
            </div>
            <p className="stat-description">Estadía promedio</p>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Métodos de Pago</h3>
            <span className="icon">
              <CreditCard size={18} />
            </span>
          </div>
          <div className="card-content">
            <div className="stat-value">
              E: {ticketsEfectivo} | T: {ticketsTransferencia} | TC: {ticketsTC}
            </div>
            <p className="stat-description">Efectivo, Transferencia, Tarjeta</p>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="charts-container">
        <div className="card chart-card">
          <div className="card-header">
            <h3>{obtenerTituloGrafico()}</h3>
          </div>
          <div className="card-content chart-content">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={datosGrafico}>
                <defs>
                  <linearGradient id="colorIngresos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#e17b1d" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#e17b1d" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="fecha" />
                <YAxis />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="ingresos"
                  stroke="#d22c5d"
                  fillOpacity={1}
                  fill="url(#colorIngresos)"
                  name="Ingresos ($)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card chart-card">
          <div className="card-header">
            <h3>Distribución de Pagos</h3>
          </div>
          <div className="card-content chart-content">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={datosTipoPago}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {datosTipoPago.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Tabla de tickets recientes */}
      <div className="card table-card">
        <div className="card-header">
          <h3>Tickets Recientes del Período</h3>
        </div>
        <div className="card-content">
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Placa</th>
                  <th>Entrada</th>
                  <th>Salida</th>
                  <th>Duración</th>
                  <th>Monto</th>
                  <th>Tipo Pago</th>
                </tr>
              </thead>
              <tbody>
                {ticketsRecientes.map((ticket) => (
                  <tr key={ticket.id}>
                    <td>{ticket.id}</td>
                    <td className="font-medium">{ticket.placa}</td>
                    <td>{formatearFechaHora(ticket.hentrada)}</td>
                    <td>{formatearFechaHora(ticket.hsalida)}</td>
                    <td>{calcularDuracion(ticket.hentrada, ticket.hsalida)}</td>
                    <td>${(ticket.monto || 0).toFixed(2)}</td>
                    <td>
                      {ticket.tipo_pago === "E"
                        ? "Efectivo"
                        : ticket.tipo_pago === "T"
                        ? "Transferencia"
                        : "Tarjeta"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Estadistica